import { Component } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { ISubscription } from "../../../../../../shared/interfaces/subscription.interface";
import { SubscriptionApiService } from "../../services/subscription-api.service";
import { EMasriVerificationStatus } from "../../enums/masri-verification-status.enum";
import { ISubscriptionVerificationResult } from "../../interfaces/subscription-verification-result.interface";
import { EEntityType } from "../../../../../../shared/enums/entity-type.enum";
import { concatName } from "../../../../../../shared/helpers/concat-name.helper";

@Component({
  selector: 'app-verify-subscription-popup',
  templateUrl: './verify-subscription-popup.component.html',
  styleUrls: ['./verify-subscription-popup.component.scss']
})
export class VerifySubscriptionPopupComponent {

  /** The subscription being verified */
  subscription: Partial<ISubscription>;

  /** The result of the verification process */
  result: ISubscriptionVerificationResult;

  /** Expose enum to the template */
  protected readonly EMasriVerificationStatus = EMasriVerificationStatus;

  /**
   * Initializes the popup component and starts the verification request.
   * Closes the dialog if the subscription ID is missing.
   *
   * @param ref Reference to the dynamic dialog
   * @param config Configuration injected into the dialog, expected to contain a subscription
   * @param subscriptionApiService Service to perform subscription verification via API
   */
  constructor(
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private subscriptionApiService: SubscriptionApiService
  ) {
    // Close the dialog if no subscription ID is provided
    if (!this.config.data.subscription.id) {
      this.ref.close();
    }

    this.subscription = this.config.data.subscription as Partial<ISubscription>;

    // Set initial result to loading status with the user's name
    this.result = {
      status: EMasriVerificationStatus.LOADING,
      name: this.getUserName(this.subscription)
    };

    this.sendVerifyRequest(this.subscription.id as string);
  }

  /**
   * Gets the user-friendly name for the subscription, depending on entity type.
   *
   * @param subscription Partial subscription object
   * @returns Full name string
   */
  getUserName({ profile, user }: Partial<ISubscription>): string {
    switch (profile?.entityType) {
      case EEntityType.PERSONAL:
        return concatName(profile.personalDetails?.fullName);
      case EEntityType.CORPORATION:
        return profile.corporationDetails?.name || '';
      default:
        return [user?.name, user?.surname].filter(Boolean).join(' ');
    }
  }

  /**
   * Sends the MASRI verification request for the given subscription ID.
   * Updates the result based on the response or error.
   *
   * @param id The subscription ID to verify
   */
  sendVerifyRequest(id: string) {
    this.subscriptionApiService.verifySubscription(id).subscribe({
      next: ({ data }) => {
        // Use the returned data, or fallback to error state
        this.result = data ?? { status: EMasriVerificationStatus.ERROR };
      },
      error: () => {
        // On error, set the result status to error
        this.result = { status: EMasriVerificationStatus.ERROR };
      }
    });
  }

  /**
   * Closes the popup dialog and returns the final verification status.
   */
  close() {
    this.ref.close(this.result.status);
  }
}
